/**
 * **어드민 역할 한 곳** — 역할 id·표시명·로그인 뒤 홈·플레이스 생명주기·계정 범위 축을 이 파일이 답한다.
 *
 * 콜팝 역할 4종(`callpopAdmin`·`placeMng`·`operatorMng`·`agencyMng`)이 들어오면서 `authId` 문자열 분기가
 * 화면마다 늘어날 자리였다. 종전에도 `MainView`·`UserInfoButton`·`PlaceListView` 가 각자 `'custMng' == authId`
 * 를 적고 있었고, 역할이 넷 늘면 같은 비교가 네 배로 복제된다 — 그러면 한 화면만 고쳐도 다른 화면의 판정이
 * 조용히 갈린다. 그래서 「이 역할은 어디로 가는가 · 무엇을 바꿀 수 있는가」를 순수 함수 한 벌로 모았다.
 *
 * ## 서버가 정본이고 여기는 그 어휘의 사본이다
 *
 * 메뉴·버튼 노출의 정본은 서버의 리소스 집합(`sys_auth_x_resources`)이고 화면은 `/mainMenu` 와
 * `sa_actions` 가 준 것만 그린다 — 이 파일은 그 표를 다시 적지 않는다. 여기 있는 것은 **리소스로
 * 표현되지 않는 세 가지**뿐이다:
 *
 * 1. 로그인 뒤 갈 곳([homePathOf]) — 서버가 내리지 않는다.
 * 2. 플레이스의 **필드별** 축([canManagePlaceLifecycle]·[canManagePlaceOwnerPartner])
 *    — 같은 `PUT /place/*` 안에서 그 값이 바뀔 때만 403 이 되므로 리소스로는 갈리지 않는다. 판정 정본은 서버
 *    `CallerScope.canManagePlaceLifecycle()`·`isInternal` 이고(B2 서버 보고서 §4 · B7-a ⑥) 여기는 **미리
 *    비활성**으로 같은 말을 한다. 조건해제(특별) 지정은 여기 없다 — 서버가 플레이스 응답의 `capabilities` 로
 *    답한다(`utils/placeServiceArea` 의 `canDesignateConditionExemptOf`, R-H). 역할 이름으로는 범위에 묶인 콜팝
 *    관리자를 가를 수 없어 스위치 한 번이 403(=로그아웃)이 됐다.
 * 3. 역할 → 계정 범위 축([scopeTypeOfRole]) — 계정 발급 화면이 무엇을 입력받아야 하는지, 그리고 그 칸이
 *    **비면 안 되는지**([requiredScopeTypesOf]). 뒤엣것의 판정 정본은 서버 `AccountIssuePolicy.check` 이고
 *    여기는 같은 말을 보내기 전에 그 칸에서 한다.
 *
 * 발급 사슬(누가 어떤 역할을 줄 수 있는가)은 **적지 않는다**. 서버 `AccountIssuePolicy` 가 판정하고
 * 화면은 그 403 문구를 그대로 보여 준다(B2 브리프 §설계 계정 발급).
 *
 * 순수 — I/O·전역 상태를 읽지 않는다(단위 테스트가 앱 번들 밖에서 그대로 읽는다).
 */
// 확장자를 적는 이유: 이 파일과 아래 import 는 단위 테스트가 앱 번들 밖(node)에서 그대로 읽는다 —
// node ESM 은 상대 경로의 확장자를 추론하지 않는다.
import { CALLPOP_PATH } from './callpopOrg.js'

/** 역할 id — 서버 `sys_auth.auth_id` · `CallerRole` 과 같은 어휘 */
export const ADMIN_ROLE = {
  ADMIN: 'admin',
  MNG: 'mng',
  CUST_MNG: 'custMng',
  STORE_MNG: 'storeMng',
  CALLPOP_ADMIN: 'callpopAdmin',
  PLACE_MNG: 'placeMng',
  OPERATOR_MNG: 'operatorMng',
  AGENCY_MNG: 'agencyMng',
}

/** 화면 경로 — 역할별 홈·계정 정보 화면이 같은 문자열을 두 번 적지 않게 한다 */
export const ADMIN_PATH = {
  PLACE_LIST: '/v/business/place/list',
  NOTI_FOR_CUST: '/v/business/noti/list_for_cust',
  DASHBOARD: '/v/system/dashboard/show',
  ACCOUNT_INFO: '/v/system/account/info',
  USER_CUST: '/v/business/user/cust',
  USER_STORE: '/v/business/user/store',
}

/**
 * 역할 표시명 — 서버 `sys_auth.auth_nm` 과 같은 낱말.
 *
 * 계정 화면은 `GET /auth` 가 준 이름을 먼저 쓰고, 그 조회가 막힌 역할(콜팝 관리자는 시스템 관리
 * 리소스가 없다)에서만 이 표로 목록을 세운다 — [adminRoleCatalog] 참고.
 */
export const ADMIN_ROLE_NM = {
  [ADMIN_ROLE.ADMIN]: '슈퍼 관리자',
  [ADMIN_ROLE.MNG]: '관리자',
  [ADMIN_ROLE.CUST_MNG]: '사업장 관리자',
  [ADMIN_ROLE.STORE_MNG]: '매장 관리자',
  [ADMIN_ROLE.CALLPOP_ADMIN]: '콜팝 관리자',
  [ADMIN_ROLE.PLACE_MNG]: '플레이스 관리자',
  [ADMIN_ROLE.OPERATOR_MNG]: '운영사',
  [ADMIN_ROLE.AGENCY_MNG]: '관리점',
}

/**
 * 플레이스 후보 셀렉트에 **세우는** 옵션 수의 상한 — 바인딩된 것은 세지 않는다([placeOptionsOf]).
 *
 * `GET /place` 는 아직 페이징이 없어 전량을 내린다(서버 원장). 상한의 정본은 서버지만, 그 전까지 화면이
 * 수천 개의 옵션 노드를 그리지 않게 앞에서 자른다 — 이미 묶인 값은 이 상한과 무관하게 남는다(잘리면
 * 태그가 id 로 되돌아간다).
 */
export const PLACE_OPTION_MAX = 200

/** 화면 문구 한 곳 — 컴포넌트 안에 한글을 다시 적지 않는다 */
export const ROLE_TEXT = {
  roleLabel: '역할',
  /**
   * 빈 `auths` 의 뜻은 **생성과 수정이 다르다** — 서버 `AccountController` 가 그렇게 갈라 두었다.
   * `add` 는 `resolveAuths` 로 빈 `auths` 를 `mng` 하나로 채우고, `up` 은 `resolveAuthsForUpdate` 로
   * **대상의 현재 역할을 그대로 유지**한다(`ObjectUtils.isEmpty` 라 `null` 도 `[]` 도 같은 갈래다).
   * 그러니 수정에서 역할이 사라지는 경로는 없고, 「비우면 관리자」는 생성에서만 참이다.
   */
  rolePlaceholderNew: '역할 선택 (비우면 관리자)',
  rolePlaceholderEdit: '역할 선택 (비우면 역할 그대로)',
  /**
   * 「권한 설정」 팝업에서 **전부 해제하고 저장**할 때의 답 — 입력 폼의 저장은 막지 않는다.
   *
   * 그 팝업이 고치는 것은 역할 축뿐이라, 전부 해제한 저장은 서버가 현재 역할을 유지해 **아무것도 바꾸지
   * 않은 채 「수정 성공」**이 뜬다. 반대로 입력 폼은 비밀번호·연락처를 함께 보내므로 역할을 비운 저장에도
   * 할 일이 있다 — 거기서 막으면 역할 축과 무관한 수정까지 함께 막힌다.
   */
  roleRequiredOnAuthDialog:
    '역할을 최소 한 개 고르세요. 전부 해제하고 저장하면 역할은 그대로 유지되어 아무것도 바뀌지 않습니다.',
  roleHint: '발급할 수 있는 역할인지는 저장할 때 서버가 판정합니다.',
  /** 서버가 `issuable:false` 라고 말한 역할의 까닭 — 목록에서 빼지 않고 **왜 못 고르는지**를 말한다 */
  roleNotIssuable: '이 계정으로는 발급할 수 없는 역할입니다.',
  /** 목록의 출처가 사본일 때만 덧붙는 한 줄 — [adminRoleCatalog] 의 **최후 수단** 갈래를 화면이 숨기지 않는다 */
  roleCatalogFallback: '역할 목록을 서버에서 읽지 못해 기본 목록을 보여 줍니다. 발급 가능한 역할과 다를 수 있습니다.',
  scopePlaceLabel: '관리 플레이스',
  scopePlacePlaceholder: '플레이스명 또는 URL명으로 검색',
  scopeOperatorLabel: '관리 운영사',
  scopeOperatorPlaceholder: '운영사 선택',
  scopeAgencyLabel: '관리 관리점',
  scopeAgencyPlaceholder: '관리점명 또는 코드로 검색',
  /**
   * 고른 역할이 요구하는 소속 칸을 **비운 채 저장**하려 할 때 그 칸에 적는 문장([missingScopesOf]).
   *
   * 서버가 같은 저장을 거절할 때의 문장과 **같은 낱말**이다(`AccountIssuePolicy.check` — 역할 이름은
   * [ADMIN_ROLE_NM], 칸 이름은 위 세 라벨). 화면이 막든 서버가 막든 직원은 같은 말을 본다.
   *
   * 문장을 축마다 통째로 적는 이유: 조사(을·를)가 낱말의 받침에 따라 갈린다 — 이름과 조사를 조립하면
   * 「관리점를」이 나온다(리포 관행: 이름 뒤 조사를 만들어 붙이지 않는다).
   */
  scopePlaceRequired: '플레이스 관리자 계정은 관리 플레이스를 골라야 합니다.',
  scopeOperatorRequired: '운영사 계정은 관리 운영사를 골라야 합니다.',
  scopeAgencyRequired: '관리점 계정은 관리 관리점을 골라야 합니다.',
  // 못 읽었을 때의 문장 — 서버가 403 문구를 주면 그것이 먼저고, 이 둘은 부르지도 못한 경우의 답이다
  accountReadDenied: '이 계정은 열 수 없습니다. 발급할 수 있는 역할의 계정만 수정·권한 설정할 수 있습니다.',
  /** 403 이 **아닌** 실패 — 「권한 밖」과 다른 말이므로 권한 안내를 빌려 쓰지 않는다(`READ_DENIED.FAILED`) */
  accountReadFailed: '계정 정보를 읽지 못했습니다. 잠시 뒤 다시 시도해 주세요.',
  /**
   * 후보를 **못 읽는** 세 축이 같은 문장을 쓴다 — 축 이름을 앞에 붙이지 않는다.
   *
   * 붙이면 「플레이스을(를)」 같은 조사를 축마다 판단해야 하고(리포 관행: 이름 뒤 조사를 쓰지 않는다),
   * 세 벌의 거의 같은 문장이 서로 다르게 고쳐진다. 어느 칸의 일인지는 그 칸에 붙어 있다는 사실이 말한다.
   */
  scopeReadDenied: '조회 권한이 없어 이미 묶인 범위를 그대로 둡니다.',
  /**
   * 생명주기 축을 못 바꾸는 역할이 보는 문구 — **허용 집합을 그대로 말한다**([PLACE_LIFECYCLE_ROLES]).
   *
   * 종전 문구는 「콜팝 관리자만 변경할 수 있습니다」였는데 실제 허용은 넷(오더엔 내부 둘·콜팝 관리자·
   * 사업장 관리자)이다. 이 문구를 보는 역할은 `placeMng` 뿐이고, 그 사람에게 「콜팝 관리자만」은
   * 「콜팝 관리자에게 부탁하라」로 읽힌다 — 사업장 관리자도 바꿀 수 있다는 사실이 사라진다(감사 §4-10).
   */
  lifecycleDenied: '오더엔 내부 관리자·콜팝 관리자·사업장 관리자만 변경할 수 있습니다.',
  /**
   * 조건해제(특별) 지정이 잠긴 사람이 보는 문구 — **서버 규칙의 허용 집합을 말한다**(`CallerScope.canDesignateConditionExempt`:
   * 오더엔 내부 + 범위에 묶이지 않은 콜팝 관리자). 잠금 여부는 서버의 `capabilities` 가 정한다(`utils/placeServiceArea`).
   *
   * [lifecycleDenied] 를 빌려 쓰면 사업장 관리자에게 「사업장 관리자도 바꿀 수 있다」는 거짓을 말한다 —
   * 이 축에서 빠진 것이 바로 그 역할이다(R-D1 S1).
   *
   * 「콜팝 관리자」라고만 하면 **범위에 묶인 콜팝 관리자**에게 거짓이다 — 그 사람도 잠긴 채 이 문구를 본다(R-J).
   * 문장은 보는 사람의 자격이 아니라 서버 규칙의 집합을 말하므로, `capabilities` 칸이 없는 옛 응답(모두 잠금)에서도
   * 같은 문장이 참이다 — 집합은 S1(appserver `6833c656`)부터 같고 칸(`2573d8bb`)은 그 뒤에 왔다.
   */
  conditionExemptDenied: '오더엔 내부 관리자와 범위에 묶이지 않은 콜팝 관리자만 바꿀 수 있습니다.',
}

/**
 * 계정 발급 쓰기 요청의 axios config — **이 403 은 세션을 끝내지 않는다**(규약 §16.170 옵트아웃).
 *
 * 계정 저장의 403 은 「그 역할·범위를 줄 수 없다」는 서버의 **답**이지 「세션이 무효다」가 아니다.
 * 기본값대로 두면 발급 사슬을 벗어난 저장 한 번이 발급자를 로그아웃시켜, 브리프가 요구한
 * 「서버 거부 문구를 그대로 보여 준다」가 아예 불가능해진다. 대체 동작은 그 문구를 폼과 토스트에
 * 그대로 띄우는 것이다 — 토스트는 그대로 두므로 `showErrorMessage` 는 켜 둔다.
 */
export const ACCOUNT_WRITE_CONFIG = { logoutOn403: false }

/** 계정 범위 축 — 서버 `ScopeType.ISSUABLE`(PLACE·OPERATOR·AGENCY)과 같은 세 축만 화면이 입력받는다 */
export const SCOPE_TYPE = {
  PLACE: 'PLACE',
  OPERATOR: 'OPERATOR',
  AGENCY: 'AGENCY',
}

/** 축 표시 순서 — 폼도 페이로드도 이 순서를 쓴다 */
const SCOPE_TYPE_ORDER = [SCOPE_TYPE.PLACE, SCOPE_TYPE.OPERATOR, SCOPE_TYPE.AGENCY]

/** 축 → 그 칸이 비었을 때의 문장([ROLE_TEXT] 의 세 문장) */
const SCOPE_REQUIRED_TEXT = {
  [SCOPE_TYPE.PLACE]: ROLE_TEXT.scopePlaceRequired,
  [SCOPE_TYPE.OPERATOR]: ROLE_TEXT.scopeOperatorRequired,
  [SCOPE_TYPE.AGENCY]: ROLE_TEXT.scopeAgencyRequired,
}

const HOME_PATH = {
  [ADMIN_ROLE.CUST_MNG]: ADMIN_PATH.NOTI_FOR_CUST,
  [ADMIN_ROLE.STORE_MNG]: ADMIN_PATH.DASHBOARD,
  // 콜팝 관리자·플레이스 관리자의 홈은 플레이스 목록이다(B2 브리프 §설계 메뉴)
  [ADMIN_ROLE.CALLPOP_ADMIN]: ADMIN_PATH.PLACE_LIST,
  [ADMIN_ROLE.PLACE_MNG]: ADMIN_PATH.PLACE_LIST,
  // 운영사·관리점의 홈은 B7-b 가 열었다 — 「준비 중」 화면은 이 회차로 사라졌다.
  // 운영사는 자기 조직(운영사·관리점)이 할 일의 출발점이고, 관리점에게는 조직 화면이 없어 관리 매장 목록이 홈이다.
  [ADMIN_ROLE.OPERATOR_MNG]: CALLPOP_PATH.ORG,
  [ADMIN_ROLE.AGENCY_MNG]: CALLPOP_PATH.STORES,
}

/**
 * **오더엔 내부 역할** — 서버 `CallerRole.INTERNAL_ROLES`(= `CallerScope.isInternal`)와 같은 어휘다.
 *
 * 「이 사람은 오더엔 내부 관리자인가」는 화면마다 되풀이되는 **한 질문**이다: 플레이스의 소유 파트너를
 * 고를 수 있는가([canManagePlaceOwnerPartner]) · 사업장 트리의 「비활성 매장 관리」 탭을 여는가 ·
 * 플레이스 목록의 사업장 열을 보는가. 서버에서도 그 셋이 같은 술어(`CallerScopeResolver.isInternal`)를
 * 지나므로 화면이 역할 문자열을 각자 비교하면 한 곳만 고쳐도 판정이 조용히 갈린다 — 실제로 사업장
 * 트리의 탭이 `authId === 'mng'` 정확 일치라 서버가 열어 둔 `admin` 에게 보이지 않았다.
 */
export const INTERNAL_ROLES = [ADMIN_ROLE.ADMIN, ADMIN_ROLE.MNG]

/**
 * 플레이스 **생명주기**(생성·삭제·상태·자동편입)를 바꿀 수 있는 역할. 조건해제는 이 축이 아니다(서버의
 * `capabilities` 가 답한다 — 머리말 2).
 *
 * 서버 `CallerScope.canManagePlaceLifecycle()` 과 같은 집합이다(보고서 §4 표). **내부 역할 + 콜팝
 * 관리자 + 사업장 관리자**이고, [ROLE_TEXT.lifecycleDenied] 가 그 집합을 그대로 말한다. `custMng` 이
 * 든 것은 이행 전 레거시로 서버와 같은 이유다 — 리얼 콜팝 플레이스가 아직 고객사 158 의 `custMng`
 * 계정으로 운영된다. 플레이스 화면에 닿는 역할은 이 넷과 `placeMng` 뿐이므로(리소스 표) 열거로 충분하다.
 */
const PLACE_LIFECYCLE_ROLES = INTERNAL_ROLES.concat([ADMIN_ROLE.CALLPOP_ADMIN, ADMIN_ROLE.CUST_MNG])

/**
 * **콜팝 사업 역할** — 서버 `CallerRole.CALLPOP_ROLES`(`CallerScope.isCallpop`)와 같은 집합이다.
 *
 * 이 집합이 여기 있는 이유는 하나뿐이다: **등록 폼이 만들 플레이스의 소유**를 서버와 같은 말로 답하기
 * 위해서다(`PlacePartnerRules.defaultOwnerOf` — 콜팝 역할이 만들면 콜팝 소유, 그 밖은 소유 없음).
 * 저장된 플레이스의 소유 여부는 화면이 묻지 않고 서버가 `callpopOwned` 로 답한다
 * (`placeServiceArea.isCallpopOwned`) — 역할로 추정하는 자리를 늘리지 않는다.
 *
 * **묻는 주체도 서버와 같아야 한다** — [hasCallpopRole] 은 역할 **목록 전체**를 본다. 서버가
 * `roles.any { it in CALLPOP_ROLES }` 이고 겸직을 명시적으로 전제하기 때문이다(`CallerScope.isCallpop`
 * 주석: 「고객사·매장에 묶인 계정에 콜팝 관리자를 겸직시켜도…」). 이 파일의 다른 술어
 * ([canManagePlaceLifecycle]·[canManagePlaceOwnerPartner])는 단일 역할(`auths[0]`)을 주체로 쓰는데,
 * 그 둘은 **컨트롤을 미리 잠그는** 보수적 방향이라 어긋나도 덜 주는 쪽으로 틀린다. 반면 이 술어는
 * **저장 본문의 내용**(콜팝 축을 싣는가·「소유 없이 등록됩니다」를 약속하는가)을 정하므로 어긋나면
 * 화면이 서버가 할 일의 반대를 말한다. 그래서 이 하나만 목록 주체로 둔다.
 */
const CALLPOP_ROLES = [ADMIN_ROLE.CALLPOP_ADMIN, ADMIN_ROLE.PLACE_MNG, ADMIN_ROLE.OPERATOR_MNG, ADMIN_ROLE.AGENCY_MNG]

/** 역할이 요구하는 범위 축 — 없으면 `null`(콜팝 관리자는 전 플레이스를 보므로 바인딩이 없다) */
const SCOPE_TYPE_OF_ROLE = {
  [ADMIN_ROLE.PLACE_MNG]: SCOPE_TYPE.PLACE,
  [ADMIN_ROLE.OPERATOR_MNG]: SCOPE_TYPE.OPERATOR,
  [ADMIN_ROLE.AGENCY_MNG]: SCOPE_TYPE.AGENCY,
}

/** 역할 표시명 — 모르는 역할은 id 를 그대로 보여 준다(감추지 않는다) */
export function adminRoleNm(authId) {
  if (!authId) return ''
  return ADMIN_ROLE_NM[authId] || authId
}

/**
 * 계정의 역할 배열(`auths`) → 역할 id 목록. 서버 응답도 폼 상태도 이 한 곳으로 좁힌다.
 *
 * @param {?Array} auths `[{authId, authNm}]`
 * @returns {Array<string>}
 */
export function authIdsOf(auths) {
  const rows = Array.isArray(auths) ? auths : []

  return rows
    .map((auth) => (auth && auth.authId) || '')
    .filter(Boolean)
    .filter((authId, index, all) => all.indexOf(authId) === index)
}

/**
 * 이 계정에 **콜팝 사업 역할이 하나라도** 있는가 — 서버 `CallerScope.isCallpop` 과 같은 답([CALLPOP_ROLES]).
 *
 * 역할 배열을 받는 이유는 [CALLPOP_ROLES] 주석에 있다(겸직 계정에서 서버와 답이 갈리면 화면이 서버가
 * 할 일의 반대를 말한다). 목록으로 좁히는 일은 [authIdsOf] 가 이미 하므로 호출부는 세션이 준 `auths` 를
 * 그대로 넘긴다 — 화면이 배열을 다시 훑지 않는다.
 *
 * @param {?Array} auths 세션의 역할 배열(`sa_account.auths` = `[{authId, authNm}]`)
 */
export function hasCallpopRole(auths) {
  return authIdsOf(auths).some((authId) => CALLPOP_ROLES.indexOf(authId) > -1)
}

/**
 * 이 계정에 **오더엔 내부 역할이 하나라도** 있는가 — 서버 `CallerScope.isInternal` 과 같은 주체다.
 *
 * [hasCallpopRole] 과 같은 이유로 목록을 본다: 서버가 `roles.any { it in INTERNAL_ROLES }` 이므로
 * 첫 역할만 보면 **겸직 계정에서 답이 갈린다**. 실제로 첫 역할이 `custMng` 이고 `mng` 을 겸직한 계정이
 * 사업장 트리에서 매장을 등록할 때, 서버는 관리자 엔드포인트를 열어 주는데 화면은 소속 사업장 칸을
 * 그리지 않아 **사업장 없는 매장**이 만들어졌다.
 *
 * @param {?Array} auths 세션의 역할 배열(`sa_account.auths` = `[{authId, authNm}]`)
 */
export function hasInternalRole(auths) {
  return authIdsOf(auths).some(isInternalRole)
}

/**
 * 이 계정이 매장의 **정산 계좌**를 고칠 수 있는가 — [canEditSettlementAccount] 의 목록 주체판.
 * 겸직으로 `admin` 을 가진 계정은 첫 역할이 무엇이든 고칠 수 있다(서버도 역할 목록으로 판정한다).
 */
export function hasSettlementAccountRole(auths) {
  return authIdsOf(auths).some(canEditSettlementAccount)
}

/**
 * 계정 발급 화면의 역할 목록 — 발급 셀렉트와 「권한 설정」 표가 **같은 목록**을 본다.
 *
 * 서버가 준 `GET /auth` 행이 정본이다(권한 관리로 따로 만든 롤까지 들어 있다). 그 조회가 막힌 역할
 * (콜팝 관리자)에서만 [ADMIN_ROLE_NM] 로 목록을 세운다 — 목록이 비면 역할을 못 고르고, 그러면
 * 브리프가 요구한 계정 발급 자체가 불가능해진다.
 *
 * **사용 중지(`useYn='N'`) 역할은 빼되, 이미 그 계정에 붙어 있으면 남긴다**(`keepIds`). 앞은 이 화면의
 * 목적이 발급이기 때문이고, 뒤는 목록이 곧 선택의 출처이기 때문이다 — 빠진 역할은 「권한 설정」 표에서
 * 체크될 자리가 없어 저장 한 번에 **조용히 사라진다**([scopeTypesOf] 가 값이 든 축을 남기는 것과 같은 이유).
 *
 * 「서버가 답했는가」와 「거른 결과가 비었는가」는 **다른 질문**이다([hasServerRoleRows]). 둘을 한
 * 표현식에 섞으면 서버가 '전부 사용 중지'라고 말한 자리에 사본 여덟 개를 `useYn:'Y'` 로 세우는 반전이
 * 생긴다 — 출처를 가르는 질문은 거르기 **전에** 끝낸다.
 *
 * **`issuable` 은 서버 행의 값을 그대로 지나간다** — 이 함수는 그것을 읽지도 거르지도 않는다(D1).
 * 「이 계정이 발급할 수 있는가」의 판정은 서버 `AccountIssuePolicy` 하나이고, 화면이 그 사슬을 역할
 * 문자열로 다시 세우면 두 판정이 갈라진다. 목록에서 **빼지 않는 것**도 같은 이유다 — 없는 역할은
 * 까닭도 말하지 못하고, 그 필드를 모르는 옛 서버에서는 뺄셈이 전부 틀린다([roleIssuable]).
 *
 * @param {?Array} authRows `GET /auth` 응답 (`[{authId, authNm, useYn, issuable}]`)
 * @param {?Array<string>} keepIds 이 계정에 이미 붙은 역할 id — 사용 중지여도 목록에 남긴다
 * @returns {Array<{authId: string, authNm: string, useYn: string, issuable: ?boolean}>}
 */
export function adminRoleCatalog(authRows, keepIds) {
  const rows = Array.isArray(authRows) ? authRows : []
  const keep = Array.isArray(keepIds) ? keepIds : []

  if (hasServerRoleRows(rows)) {
    return rows
      .filter((row) => row && row.authId)
      .filter((row) => row.useYn !== 'N' || keep.indexOf(row.authId) > -1)
      .map((row) => Object.assign({}, row, { authNm: row.authNm || adminRoleNm(row.authId) }))
  }

  const known = Object.keys(ADMIN_ROLE_NM)
  const unknownKept = keep.filter((authId) => known.indexOf(authId) === -1)

  return known.concat(unknownKept).map((authId) => ({ authId, authNm: adminRoleNm(authId), useYn: 'Y' }))
}

/**
 * 이 계정이 **발급할 수 있는 역할인가** — 서버가 `GET /auth` 행에 실은 값을 읽을 뿐이다(D1).
 *
 * `issuable` 이 **없으면 고를 수 있다**. 그 필드를 내려 주지 않는 서버에서 목록을 통째로 잠그면 계정
 * 발급 화면이 멈춘다. 저장 경계는 그대로 서버가 지키므로(403), 이 술어가 틀려도 권한은 새지 않는다 —
 * 여기는 **까닭을 미리 말하는 자리**이지 두 번째 판정이 아니다.
 *
 * @param {?object} role [adminRoleCatalog] 의 한 행
 */
export function roleIssuable(role) {
  return !role || role.issuable !== false
}

/**
 * 서버가 역할 목록을 **답했는가** — 목록의 출처를 가르는 하나의 질문이다.
 *
 * [adminRoleCatalog] 의 사본 갈래와 화면의 「기본 목록입니다」 안내([ROLE_TEXT.roleCatalogFallback])가
 * 같은 술어를 읽는다. 화면이 따로 세면 목록은 서버 것인데 안내는 사본이라고 말하는 어긋남이 생긴다.
 *
 * @param {?Array} authRows `GET /auth` 응답
 */
export function hasServerRoleRows(authRows) {
  const rows = Array.isArray(authRows) ? authRows : []

  return rows.some((row) => row && row.authId)
}

/** 로그인 뒤 갈 곳 — `null` 이면 그 역할은 현재 화면에 머문다(`admin`·`mng` 는 종전 그대로다) */
export function homePathOf(authId) {
  return HOME_PATH[authId] || null
}

/**
 * 「내 계정 정보」 경로 — `null` 이면 그 버튼을 그리지 않는다(역할을 모를 때뿐이다).
 *
 * **콜팝 역할 4종도 연다.** 한때 넷을 막아 두었던 근거(「자기 자신을 못 읽는다」)는 서버와 정면으로
 * 어긋났다 — `AccountIssuePolicy.checkRead` 는 `isSelf` 면 언제나 허용이고, 리소스도 `GET/PUT /account/*`
 * 는 **네 역할 모두 역할 파일**(`db_changes_2026-09-15_callpop-roles-scope.sql`)이 준다. 한 URL 은 한
 * 파일이 전부 적는다는 규칙이고(appserver triage A5), shell 파일은 그 행들을 갖지 않는다 — 거기에
 * `/account/*` 를 도로 적으면 그 파일의 URL 단위 DELETE 가 역할 파일 몫을 지운다. 그 리소스의 이유가
 * 「첫 로그인 비밀번호 변경이 기존 Account 정책이라 이 경로가 없으면 새 계정이 막힌다」이므로, 버튼을
 * 지우면 **새로 발급한 계정이 자기 비밀번호를 못 바꾼다**.
 *
 * 저장 전 중복 확인(`PATCH /account/chkTel/*`·`/account/chkEmail/*`)의 리소스는 SQL `017` 이 준다.
 */
export function accountInfoPathOf(authId) {
  if (!authId) return null
  if (authId === ADMIN_ROLE.CUST_MNG) return ADMIN_PATH.USER_CUST
  if (authId === ADMIN_ROLE.STORE_MNG) return ADMIN_PATH.USER_STORE
  return ADMIN_PATH.ACCOUNT_INFO
}

/** 오더엔 내부 관리자인가 — [INTERNAL_ROLES] */
export function isInternalRole(authId) {
  return INTERNAL_ROLES.indexOf(authId) > -1
}

/**
 * **매장의 정산 계좌(은행·계좌번호·예금주)를 고칠 수 있는가** — 내부 역할 중에서도 `admin` 뿐이다.
 *
 * 다른 사업자정보 칸(업태·종목·이메일·팩스·첨부)과 달리 이 셋은 **돈이 실제로 가는 곳**이고,
 * 서버 `POST/PUT /store/mng` 에는 필드 단위 가드가 없어 본문에 실리면 그대로 저장된다. 매장 저장
 * 자체는 `mng` 에게 열려 있으므로(같은 액션), 그 역할에게 이 칸을 그리면 곧 쓸 수 있게 된다.
 *
 * **서버에 그 축의 액션이 생기기 전까지 화면이 좁게 잡는다.** 서버가 판정을 갖게 되면 이 술어는
 * 그 액션 보유 여부로 바뀐다(`checkAuthActionAccess`) — 지금은 물을 곳이 없다.
 */
export function canEditSettlementAccount(authId) {
  return authId === ADMIN_ROLE.ADMIN
}

/** 플레이스 생성·삭제·상태·자동편입을 바꿀 수 있는가 */
export function canManagePlaceLifecycle(authId) {
  return PLACE_LIFECYCLE_ROLES.indexOf(authId) > -1
}

/**
 * 플레이스의 **소유 파트너**를 바꿀 수 있는가 — 내부 역할과 **같은 질문**이다(서버 `CallerScope.isInternal`,
 * B7-a 서버 보고서 ⑥). 생명주기와 달리 콜팝 관리자·사업장 관리자는 들지 않는다 — 그 역할은 같은 값
 * 되보내기만 통과하므로 화면이 미리 읽기로 보인다. 이름을 남겨 두는 것은 호출부가 **무엇을 묻는지**
 * 그대로 읽히게 하기 위해서고, 답은 [isInternalRole] 하나다.
 */
export function canManagePlaceOwnerPartner(authId) {
  return isInternalRole(authId)
}

/** 이 역할이 요구하는 범위 축(`null` 이면 없음) */
export function scopeTypeOfRole(authId) {
  return SCOPE_TYPE_OF_ROLE[authId] || null
}

/**
 * 고른 역할이 **요구하는** 범위 축 — 표시 순서 고정, 중복 없음. 「역할 → 축」을 읽는 한 곳이다.
 *
 * 이 축은 **소속이 하나 이상 있어야 한다.** 운영사·관리점·플레이스 관리자는 소속(범위 바인딩)으로만
 * 매장을 보므로, 비운 채 만들면 역할은 있는데 아무것도 안 보이는 계정이 된다(리얼 2026-10-12 — 총괄
 * 운영사가 「관리 운영사」를 비운 채 저장한 운영사 계정). 그래서 칸을 그리는 것([scopeTypesOf])도 필수
 * 표시도 저장 전 검사([missingScopesOf])도 이 목록에서 나온다.
 *
 * 판정 정본은 서버 `AccountIssuePolicy.check` 다(역할이 요구하는 축은 `CallerRole` 이 갖는다). 여기는
 * [roleIssuable] 처럼 **미리 말하는 자리**라, 틀려도 소속 없는 계정이 만들어지지는 않는다.
 *
 * @param {?Array<string>} authIds 고른 역할
 * @returns {Array<string>}
 */
export function requiredScopeTypesOf(authIds) {
  const ids = Array.isArray(authIds) ? authIds : []
  const wanted = ids.map(scopeTypeOfRole).filter(Boolean)

  return SCOPE_TYPE_ORDER.filter((type) => wanted.indexOf(type) > -1)
}

/**
 * 이 계정이 다루는 범위 축 — 표시 순서 고정, 중복 없음.
 *
 * **고른 역할이 요구하는 축([requiredScopeTypesOf]) ∪ 이미 값이 들어 있는 축**이다. 뒤엣것이 있는 이유:
 * 서버가 준 바인딩이 그 계정의 역할과 짝이 안 맞을 수 있는데(관리자가 붙여 둔 축), 역할만 보면 그 칸이
 * 화면에서 사라지고 저장은 **전체 교체**라 조용히 지워진다. 축 집합을 화면과 페이로드가 함께 쓰므로
 * **보이는 것이 곧 보내는 것**이고, 버리려면 사람이 그 칸을 비운다.
 *
 * @param {?Array<string>} authIds 고른 역할
 * @param {?object} scopeForm 현재 입력 폼([initScopeForm]) — 없으면 역할 축만
 */
export function scopeTypesOf(authIds, scopeForm) {
  const required = requiredScopeTypesOf(authIds)

  return SCOPE_TYPE_ORDER.filter((type) => required.indexOf(type) > -1 || hasScopeValue(scopeForm, type))
}

/**
 * 고른 역할이 요구하는데 **비어 있는** 축과 그 칸에 적을 문장 — 빈 배열이면 저장해도 된다.
 *
 * 값이 있어서 보이기만 하는 축(역할이 요구하지 않는 축)은 비워도 된다 — 그것이 그 범위를 버리는 길이다.
 * 역할을 하나도 고르지 않은 폼은 요구하는 축이 없다: 그 저장에서 계정이 갖게 될 역할은 서버가 정하고
 * (생성은 `mng`, 수정은 현재 역할 그대로) 폼은 그 역할을 모른다.
 *
 * @param {?Array<string>} authIds 고른 역할
 * @param {?object} scopeForm 현재 입력 폼([initScopeForm])
 * @returns {Array<{type: string, message: string}>}
 */
export function missingScopesOf(authIds, scopeForm) {
  return requiredScopeTypesOf(authIds)
    .filter((type) => !hasScopeValue(scopeForm, type))
    .map((type) => ({ type, message: SCOPE_REQUIRED_TEXT[type] }))
}

/** 그 축에 값이 들어 있는가 — 세 축 모두 **고른 PK 의 목록**이다 */
function hasScopeValue(scopeForm, type) {
  if (!scopeForm) return false

  return toIdList(scopeForm[type]).length > 0
}

/**
 * 빈 범위 입력 폼 — 세 축이 **같은 모양**(고른 PK 문자열의 배열)이다.
 *
 * 종전에 운영사·관리점만 자유 텍스트였던 것은 마스터 표가 없어 `scope_id` 가 임의 문자열이었기 때문이다
 * (B2 회차). B7-b 가 두 마스터를 만들면서 서버가 `scope_id` 를 **마스터 PK** 로 확정했으므로
 * (서버 보고서 §8 4) 그 갈래는 사라진다 — 사람이 코드를 타이핑해 넣을 자리가 없다.
 */
export function initScopeForm() {
  return {
    [SCOPE_TYPE.PLACE]: [],
    [SCOPE_TYPE.OPERATOR]: [],
    [SCOPE_TYPE.AGENCY]: [],
  }
}

/**
 * 플레이스 후보 → 셀렉트 옵션. **상한은 안 묶인 후보에만 건다**.
 *
 * [PLACE_OPTION_MAX] 가 막으려는 것은 「그리는 노드 수」이지 「이미 고른 값의 이름」이 아니다. 잘린 자리에
 * 바인딩이 들어 있으면 그 태그는 이름을 잃고 **숫자 id 로 되돌아간다** — 무엇에 묶였는지 못 읽는 상태이고,
 * 이 화면이 다른 갈래에서는 극구 피하는 바로 그것이다. 그래서 묶인 id 는 상한과 무관하게 전량 남기고
 * 나머지에만 상한을 건다(근본은 서버 `GET /place` 의 페이징·id 다건 조회다 — 그때 이 상한이 사라진다).
 *
 * @param {?Array} items `GET /place` 응답의 `items`(`[{id, name, urlSlug}]`)
 * @param {?Array<string>} boundIds 이 계정에 이미 묶인 플레이스 id
 * @returns {Array<{id: string, label: string}>}
 */
export function placeOptionsOf(items, boundIds) {
  const rows = (Array.isArray(items) ? items : []).filter((item) => item && item.id != null)
  const bound = toIdList(boundIds)
  const isBound = (item) => bound.indexOf(String(item.id)) > -1

  return rows
    .filter(isBound)
    .concat(rows.filter((item) => !isBound(item)).slice(0, PLACE_OPTION_MAX))
    .map((item) => ({ id: String(item.id), label: `${item.name} (${item.urlSlug})` }))
}

/** 서버가 준 `scopes` → 입력 폼. 화면이 모르는 축(CUST·STORE)은 폼에 자리가 없으므로 버린다 */
export function toScopeForm(scopes) {
  const form = initScopeForm()
  const rows = Array.isArray(scopes) ? scopes : []

  rows.forEach((row) => {
    if (!row || !row.type || row.id == null) return
    if (!form[row.type]) return
    const id = String(row.id).trim()
    if (!id) return
    if (form[row.type].indexOf(id) === -1) form[row.type].push(id)
  })

  return form
}

/**
 * 입력 폼 → 서버 계약의 `scopes`(`[{type, id}]`, **전체 교체**).
 *
 * 축 집합은 화면이 그리는 것과 **같은 함수**([scopeTypesOf])가 정한다 — 보이는 칸이 곧 보내는 값이다.
 * 축이 하나도 없으면 빈 배열이고, 그것은 「범위를 전부 지운다」는 뜻이다(서버 보고서 §3).
 */
export function toScopePayload(scopeForm, authIds) {
  const form = scopeForm || initScopeForm()

  return scopeTypesOf(authIds, form).reduce(
    (payload, type) => payload.concat(toIdList(form[type]).map((id) => ({ type, id }))),
    []
  )
}

function toIdList(value) {
  const list = Array.isArray(value) ? value : []

  return list
    .map((id) => (id == null ? '' : String(id).trim()))
    .filter(Boolean)
    .filter((id, index, all) => all.indexOf(id) === index)
}
